class AutoScrollProducts {
  constructor(options = {}) {
    this.options = {
      container: options.container || '.auto-scroll-products-container',
      itemClass: 'auto-scroll-product-item',
      triggerOffset: options.triggerOffset || 200,
      productsPerLoad: options.productsPerLoad || 4,
      animationDuration: options.animationDuration || 600,
      collection: options.collection || null,
      currentProductId: options.currentProductId || null,
      showPrice: options.showPrice !== false,
      showDescription: options.showDescription !== false,
      showAddToCart: options.showAddToCart !== false,
      ...options
    };

    this.products = [];
    this.loadedProducts = [];
    this.currentIndex = 0;
    this.isLoading = false;
    this.observer = null;
    this.container = null;

    this.init();
  }

  async init() {
    this.container = document.querySelector(this.options.container);
    if (!this.container) {
      console.warn('Auto-scroll products container not found');
      return;
    }

    await this.loadProducts();
    this.setupScrollTrigger();
    this.setupIntersectionObserver();
    this.preloadInitialProducts();
  }

  async loadProducts() {
    try {
      let url;
      if (this.options.collection) {
        url = `/collections/${this.options.collection}/products.json?limit=50`;
      } else {
        // Load from all products or a default collection
        url = '/products.json?limit=50';
      }

      const response = await fetch(url);
      const data = await response.json();
      
      // Filter out current product and ghost products
      this.products = data.products
        .filter(product => 
          product.id !== this.options.currentProductId && 
          product.product_type !== 'giftbox_ghost_product' &&
          product.available
        )
        .map(this.processProduct.bind(this));

      console.log(`Loaded ${this.products.length} products for auto-scroll`);
    } catch (error) {
      console.error('Failed to load products:', error);
    }
  }

  processProduct(product) {
    const variant = product.variants[0] || {};
    const featuredImage = product.images[0] || {};
    
    return {
      id: product.id,
      title: product.title,
      handle: product.handle,
      price: variant.price,
      compareAtPrice: variant.compare_at_price,
      available: product.available,
      vendor: product.vendor,
      productType: product.product_type,
      description: product.body_html,
      featuredImage: {
        src: featuredImage.src,
        alt: featuredImage.alt || product.title,
        width: featuredImage.width,
        height: featuredImage.height
      },
      url: `/products/${product.handle}`,
      variant: variant
    };
  }

  setupScrollTrigger() {
    let ticking = false;
    
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          this.handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  handleScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    
    // Check if we're near the bottom and should load more products
    if (documentHeight - scrollTop - windowHeight < this.options.triggerOffset) {
      this.loadMoreProducts();
    }

    // Update active product based on scroll position
    this.updateActiveProduct();
  }

  setupIntersectionObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('auto-scroll-product--visible');
            this.animateProductEntry(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '50px 0px'
      }
    );
  }

  preloadInitialProducts() {
    const initialCount = Math.min(this.options.productsPerLoad, this.products.length);
    for (let i = 0; i < initialCount; i++) {
      this.addProductToDOM(this.products[i]);
    }
    this.currentIndex = initialCount;
  }

  loadMoreProducts() {
    if (this.isLoading || this.currentIndex >= this.products.length) {
      return;
    }

    this.isLoading = true;
    const loadCount = Math.min(
      this.options.productsPerLoad,
      this.products.length - this.currentIndex
    );

    // Add products with a slight delay for smooth appearance
    for (let i = 0; i < loadCount; i++) {
      setTimeout(() => {
        const product = this.products[this.currentIndex + i];
        if (product) {
          this.addProductToDOM(product);
        }
        
        if (i === loadCount - 1) {
          this.currentIndex += loadCount;
          this.isLoading = false;
        }
      }, i * 100);
    }
  }

  addProductToDOM(product) {
    const productElement = this.createProductElement(product);
    this.container.appendChild(productElement);
    this.loadedProducts.push({ element: productElement, product });
    
    if (this.observer) {
      this.observer.observe(productElement);
    }
  }

  createProductElement(product) {
    const element = document.createElement('div');
    element.className = `${this.options.itemClass} auto-scroll-product`;
    element.dataset.productId = product.id;
    
    const discountPercentage = this.calculateDiscountPercentage(product.price, product.compareAtPrice);
    const hasDiscount = discountPercentage > 0;

    element.innerHTML = `
      <div class="auto-scroll-product__image-wrapper">
        ${hasDiscount ? `<span class="auto-scroll-product__badge">${discountPercentage}% OFF</span>` : ''}
        <a href="${product.url}" class="auto-scroll-product__image-link">
          <img 
            src="${this.getImageUrl(product.featuredImage.src, 400)}" 
            alt="${product.featuredImage.alt}"
            class="auto-scroll-product__image"
            loading="lazy"
            width="${product.featuredImage.width || 400}"
            height="${product.featuredImage.height || 400}"
          />
        </a>
        ${this.options.showAddToCart ? this.createQuickAddButton(product) : ''}
      </div>
      
      <div class="auto-scroll-product__info">
        ${this.options.showVendor && product.vendor ? `<div class="auto-scroll-product__vendor">${product.vendor}</div>` : ''}
        
        <h3 class="auto-scroll-product__title">
          <a href="${product.url}">${product.title}</a>
        </h3>
        
        ${this.options.showPrice ? this.createPriceHTML(product) : ''}
        
        ${this.options.showDescription ? this.createDescriptionHTML(product) : ''}
        
        <div class="auto-scroll-product__actions">
          <a href="${product.url}" class="auto-scroll-product__view-button button button--outline">
            View Product
          </a>
        </div>
      </div>
    `;

    return element;
  }

  createPriceHTML(product) {
    const price = this.formatMoney(product.price);
    const comparePrice = product.compareAtPrice ? this.formatMoney(product.compareAtPrice) : null;

    if (comparePrice && product.compareAtPrice > product.price) {
      return `
        <div class="auto-scroll-product__price-container">
          <span class="auto-scroll-product__price auto-scroll-product__price--sale">${price}</span>
          <span class="auto-scroll-product__price auto-scroll-product__price--compare">${comparePrice}</span>
        </div>
      `;
    }

    return `
      <div class="auto-scroll-product__price-container">
        <span class="auto-scroll-product__price">${price}</span>
      </div>
    `;
  }

  createDescriptionHTML(product) {
    if (!product.description) return '';
    
    const plainDescription = this.stripHTML(product.description);
    const truncated = this.truncateText(plainDescription, 120);
    
    return `<div class="auto-scroll-product__description">${truncated}</div>`;
  }

  createQuickAddButton(product) {
    if (!product.available) {
      return '<button class="auto-scroll-product__add-button button button--disabled" disabled>Sold Out</button>';
    }

    return `
      <button 
        class="auto-scroll-product__add-button button button--primary" 
        data-product-id="${product.id}"
        data-variant-id="${product.variant.id}"
        onclick="autoScrollProducts.addToCart(this)"
      >
        Quick Add
      </button>
    `;
  }

  async addToCart(buttonElement) {
    const variantId = buttonElement.dataset.variantId;
    const originalText = buttonElement.textContent;
    
    buttonElement.disabled = true;
    buttonElement.textContent = 'Adding...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId),
            quantity: 1
          }]
        })
      });

      if (response.ok) {
        buttonElement.textContent = 'Added!';
        buttonElement.classList.add('auto-scroll-product__add-button--success');
        
        // Trigger cart update event
        document.dispatchEvent(new CustomEvent('cart:updated'));
        
        setTimeout(() => {
          buttonElement.textContent = originalText;
          buttonElement.disabled = false;
          buttonElement.classList.remove('auto-scroll-product__add-button--success');
        }, 2000);
      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (error) {
      console.error('Add to cart failed:', error);
      buttonElement.textContent = 'Error';
      setTimeout(() => {
        buttonElement.textContent = originalText;
        buttonElement.disabled = false;
      }, 2000);
    }
  }

  updateActiveProduct() {
    const products = this.container.querySelectorAll('.auto-scroll-product');
    const viewportCenter = window.innerHeight / 2;
    
    products.forEach(product => {
      const rect = product.getBoundingClientRect();
      const isInCenter = rect.top <= viewportCenter && rect.bottom >= viewportCenter;
      
      product.classList.toggle('auto-scroll-product--active', isInCenter);
    });
  }

  animateProductEntry(element) {
    element.style.opacity = '0';
    element.style.transform = 'translateY(50px)';
    
    setTimeout(() => {
      element.style.transition = `opacity ${this.options.animationDuration}ms ease, transform ${this.options.animationDuration}ms ease`;
      element.style.opacity = '1';
      element.style.transform = 'translateY(0)';
    }, 100);
  }

  // Utility functions
  getImageUrl(src, width) {
    if (!src) return '';
    return src.replace(/\.(jpg|jpeg|png|gif|webp)/i, `_${width}x.$1`);
  }

  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    return `$${(cents / 100).toFixed(2)}`;
  }

  calculateDiscountPercentage(price, comparePrice) {
    if (!comparePrice || comparePrice <= price) return 0;
    return Math.round(((comparePrice - price) / comparePrice) * 100);
  }

  stripHTML(html) {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }

  truncateText(text, length) {
    if (text.length <= length) return text;
    return text.substring(0, length).trim() + '...';
  }

  destroy() {
    if (this.observer) {
      this.loadedProducts.forEach(({ element }) => {
        this.observer.unobserve(element);
      });
      this.observer.disconnect();
    }
    
    window.removeEventListener('scroll', this.handleScroll);
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Wait for other scripts to initialize first
  setTimeout(() => {
    const container = document.querySelector('.auto-scroll-products-container');
    if (container) {
      const options = {
        currentProductId: window.productId || null,
        collection: container.dataset.collection || null,
        productsPerLoad: parseInt(container.dataset.productsPerLoad) || 4,
        showPrice: container.dataset.showPrice !== 'false',
        showDescription: container.dataset.showDescription !== 'false',
        showAddToCart: container.dataset.showAddToCart !== 'false',
        showVendor: container.dataset.showVendor === 'true'
      };
      
      window.autoScrollProducts = new AutoScrollProducts(options);
    }
  }, 1000);
});

// Also initialize when theme sections are loaded (for Shopify admin preview)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    if (event.detail.sectionId.includes('auto-scroll-products')) {
      const container = event.target.querySelector('.auto-scroll-products-container');
      if (container && !window.autoScrollProducts) {
        const options = {
          currentProductId: window.productId || null,
          collection: container.dataset.collection || null,
          productsPerLoad: parseInt(container.dataset.productsPerLoad) || 4,
          showPrice: container.dataset.showPrice !== 'false',
          showDescription: container.dataset.showDescription !== 'false',
          showAddToCart: container.dataset.showAddToCart !== 'false',
          showVendor: container.dataset.showVendor === 'true'
        };
        
        window.autoScrollProducts = new AutoScrollProducts(options);
      }
    }
  });
  
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.detail.sectionId.includes('auto-scroll-products') && window.autoScrollProducts) {
      window.autoScrollProducts.destroy();
      window.autoScrollProducts = null;
    }
  });
}
